// ThemeProvider — pt89T (Feb 20, 2026, Randy).
//
// React context that seeds every AI Calendar surface with silo-scoped
// label overrides. Stage A ships only the plumbing + the 15-key starter
// manifest. Stage B wires the LLM-driven modal that lets an owner rewrite
// every "family/parent/kid/chore" word to fit any theme.
//
// Rules (locked with Randy):
//   • Silo-scoped only. No global theme.
//   • Every hook call uses a manifest key + implicit default fallback.
//   • Missing theme_map / malformed keys / empty siloId ⇒ default wins.
//   • Reserved words (auth / Stripe / legal) can never be overwritten;
//     the Stage-B LLM prompt enforces this list on the generator side.
//   • Theme map load is lazy — the provider tries GET /silos/{id} once
//     per silo, but if it fails the app keeps running on defaults.
//
// Usage:
//   1. Wrap a page/section: `<CalendarThemeProvider siloId={silo.id}>…`.
//   2. Inside, call `const label = useThemeLabel("calendar.title")`.
//   3. Change the map: `POST /silos/{id}/calendar-theme` (Stage B).

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { themeDefault, THEME_MANIFEST } from "@/lib/themeManifest";

const ThemeMapContext = createContext({ map: {}, reload: () => {}, loaded: false, siloId: null });

export function CalendarThemeProvider({ siloId, initialMap, skipFetch = false, children }) {
  const [map, setMap] = useState(() => (initialMap && typeof initialMap === "object") ? initialMap : {});
  const [loaded, setLoaded] = useState(!!(skipFetch || initialMap));

  const reload = useCallback(async () => {
    // Kid-token contexts pass `skipFetch` — the parent already fed us
    // the theme_map from a kid-safe endpoint (e.g. /api/kid/me).
    if (skipFetch) { setLoaded(true); return; }
    if (!siloId) { setMap({}); setLoaded(true); return; }
    try {
      // Piggyback on the silo doc — Stage B adds theme_map alongside the
      // existing fields. Failure ⇒ empty map ⇒ defaults win.
      const r = await api.get(`/silos/${siloId}`);
      const tm = (r.data && r.data.theme_map) || {};
      setMap(tm && typeof tm === "object" ? tm : {});
    } catch { setMap({}); }
    finally { setLoaded(true); }
  }, [siloId, skipFetch]);

  useEffect(() => { reload(); }, [reload]);

  // Keep the provider's map in sync when a caller updates initialMap
  // (e.g. Kid Home's /kid/me poll returns a newly-applied theme).
  useEffect(() => {
    if (skipFetch && initialMap && typeof initialMap === "object") {
      setMap(initialMap);
      setLoaded(true);
    }
  }, [initialMap, skipFetch]);

  const value = useMemo(() => ({ map, reload, loaded, siloId: siloId || null }), [map, reload, loaded, siloId]);

  return <ThemeMapContext.Provider value={value}>{children}</ThemeMapContext.Provider>;
}

/**
 * Read the current display label for a manifest key. Falls back to the
 * hardcoded default if the silo has no override.
 *
 * @param {string} key    Manifest key (see themeManifest.js).
 * @param {string} [fallback]  Optional runtime override for the default.
 * @returns {string}
 */
export function useThemeLabel(key, fallback) {
  const ctx = useContext(ThemeMapContext);
  const rawOverride = ctx?.map?.[key];
  if (rawOverride && typeof rawOverride === "string" && rawOverride.trim()) {
    // Char-budget safety: even if a bad map slipped in, don't render
    // 500-char nonsense in a `text-lg` heading.
    const budget = THEME_MANIFEST[key]?.charBudget || 999;
    return rawOverride.trim().slice(0, budget);
  }
  if (fallback && typeof fallback === "string") return fallback;
  return themeDefault(key);
}

/**
 * Read the whole theme map + a reload trigger. Used by the Stage-B
 * modal so it can flip the preview after applying a new map without
 * a full page refresh.
 */
export function useCalendarTheme() {
  return useContext(ThemeMapContext);
}

/**
 * Plain-function variant of `useThemeLabel` — takes a raw theme_map and
 * returns the effective label. Used by aggregated views (e.g. the Fan
 * Portal's "Your family" section) where each card belongs to a different
 * silo with its own theme_map, so a single React provider can't cover
 * them all. Same char-budget guard + default fallback as the hook.
 *
 * @param {object} themeMap  Silo's theme_map dict (may be null/undefined).
 * @param {string} key       Manifest key (see themeManifest.js).
 * @param {string} [fallback] Optional runtime override for the default.
 * @returns {string}
 */
export function themeLabelFromMap(themeMap, key, fallback) {
  const raw = themeMap && typeof themeMap === "object" ? themeMap[key] : null;
  if (raw && typeof raw === "string" && raw.trim()) {
    const budget = THEME_MANIFEST[key]?.charBudget || 999;
    return raw.trim().slice(0, budget);
  }
  if (fallback && typeof fallback === "string") return fallback;
  return themeDefault(key);
}
